// Backend mock in-memory del modo demo, sobre el API v2 (terminal).
//
// Atiende los endpoints que el POS consume — pareo, catálogo, órdenes, invoice, efectivo,
// historial — con estado en memoria. Las invoices se auto-confirman a los ~6 s: el POS las
// consulta por polling, así que el cambio de estado alcanza y NO hace falta un WebSocket
// falso (el `/ws/payments` real es de v1 y ni siquiera está scopeado por tenant).
//
// Formas de respuesta = las de `infrastructure/deviceApi.ts`. Nada de esto toca la red.

import type {
  DeviceInvoice,
  DeviceOrder,
  DeviceProduct,
  DeviceSale,
  DeviceSaleItem,
  PaymentMethod,
} from "../deviceApi";

// ---------- config ----------
const MXN_PER_BTC = 2_150_000; // tipo de cambio fijo del demo
const SATS_PER_MXN = 100_000_000 / MXN_PER_BTC;
const AUTO_PAY_MS = 6_000; // la invoice demo se confirma a los ~6 s
const INVOICE_TTL_MS = 120_000; // expira en 2 min

export const DEMO_DEVICE_TOKEN = "demo-device-token";
const DEMO_CODES = new Set(["demo", "demo-mode", "demonstration-mode", "modo-demo"]);

/** Mismo juego de palabras que la app móvil, para que una demo se dé igual en los dos lados. */
export function isDemoCode(code: string): boolean {
  return DEMO_CODES.has(code.trim().toLowerCase());
}

const TENANT = { id: "demo-tenant", name: "Demo Café" };
const TERMINAL = { id: "demo-terminal", name: "Demo", role: "manager" as const };

function mxnToSats(mxn: number): number {
  return Math.round(mxn * SATS_PER_MXN);
}

function money(n: number): string {
  return n.toFixed(2);
}

let seq = 0;
function id(prefix: string): string {
  seq += 1;
  return `demo-${prefix}-${seq}`;
}

function isoDaysAgo(days: number, hour = 12): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

function fakeBolt11(sats: number): string {
  // Prefijo "lnbcfake" = invoice deliberadamente NO pagable; padded para que el QR se vea
  // como un bolt11 real. Misma convención que la app móvil.
  const pad = "qpzry9x8gf2tvdw0s3jn54khce6mua7l".repeat(6);
  return `lnbcfake${sats}n1${pad}`;
}

// ---------- estado ----------
const products: DeviceProduct[] = [];
const orders = new Map<string, DeviceOrder>();
// Las lineas de cada orden: el historial las muestra y el dashboard arma con ellas el
// ranking de productos.
const orderItems = new Map<string, DeviceSaleItem[]>();
const invoices = new Map<string, DeviceInvoice>();
const sales: DeviceSale[] = [];
let seeded = false;

function seedProducts(): void {
  const base: [string, number, string][] = [
    ["Café americano", 45, "7501000000011"],
    ["Cappuccino", 65, "7501000000028"],
    ["Latte", 68, "7501000000035"],
    ["Croissant", 52, "7501000000042"],
    ["Concha", 28, "7501000000059"],
    ["Agua mineral", 30, "7501000000066"],
    ["Playera Lightning", 350, "7501000000073"],
  ];
  for (const [name, price, barcode] of base) {
    products.push({ id: id("prod"), name, price_mxn: money(price), barcode, active: true });
  }
}

function seedSales(): void {
  // Historial repartido en los últimos 7 días, mezclando efectivo y Lightning: el POS v2
  // cobra de las dos formas y un demo que sólo muestre bitcoin no representa el producto.
  type Linea = [string, number, number]; // [nombre, precio, cantidad]
  const plan: [number, number, PaymentMethod, string, Linea[]][] = [
    // [díasAtrás, hora, método, estado, líneas]
    [0, 9, "lightning", "paid", [["Café americano", 45, 1], ["Croissant", 52, 1]]],
    [0, 11, "cash", "paid", [["Latte", 68, 2]]],
    [0, 13, "lightning", "paid", [["Cappuccino", 65, 1], ["Concha", 28, 1]]],
    [0, 14, "lightning", "expired", [["Agua mineral", 30, 1]]],
    [1, 10, "cash", "paid", [["Playera Lightning", 350, 1]]],
    [1, 17, "lightning", "paid", [["Café americano", 45, 3]]],
    [2, 12, "lightning", "paid", [["Latte", 68, 1], ["Croissant", 52, 2]]],
    [3, 16, "cash", "paid", [["Cappuccino", 65, 2]]],
    [4, 9, "lightning", "paid", [["Café americano", 45, 1]]],
    [5, 15, "cash", "paid", [["Concha", 28, 4], ["Agua mineral", 30, 2]]],
    [6, 11, "lightning", "paid", [["Latte", 68, 1]]],
  ];
  for (const [day, hour, method, status, lineas] of plan) {
    const at = isoDaysAgo(day, hour);
    const items: DeviceSaleItem[] = lineas.map(([nombre, precio, cant]) => ({
      product_id: products.find((p) => p.name === nombre)?.id ?? null,
      description: nombre,
      qty: cant,
      unit_price_mxn: money(precio),
      line_total_mxn: money(precio * cant),
    }));
    const total = lineas.reduce((s, [, precio, cant]) => s + precio * cant, 0);
    const esLn = method === "lightning";
    sales.push({
      order_id: id("order"),
      status,
      payment_method: method,
      total_mxn: money(total),
      paid_at: status === "paid" ? at : null,
      created_at: at,
      invoice_id: esLn ? id("inv") : null,
      amount_sats: esLn ? mxnToSats(total) : null,
      payment_hash: esLn ? id("hash") : null,
      fx_rate: esLn ? money(MXN_PER_BTC) : null,
      expires_at: esLn ? at : null,
      items,
      discount_mxn: money(0),
    });
  }
}

function ensureSeeded(): void {
  if (seeded) return;
  seeded = true;
  seedProducts();
  seedSales();
}

/** Registra la venta en el historial, para que el demo muestre lo que el usuario acaba de hacer. */
function recordSale(order: DeviceOrder, invoice: DeviceInvoice | null): void {
  sales.unshift({
    order_id: order.id,
    status: order.status,
    payment_method: order.payment_method,
    total_mxn: order.total_mxn,
    paid_at: order.paid_at,
    created_at: order.created_at,
    invoice_id: invoice?.id ?? null,
    amount_sats: invoice?.amount_sats ?? null,
    payment_hash: invoice?.payment_hash ?? null,
    fx_rate: invoice?.fx_rate ?? null,
    expires_at: invoice?.expires_at ?? null,
    items: orderItems.get(order.id) ?? [],
    discount_mxn: order.discount_mxn,
  });
}

function scheduleAutoPay(invoice: DeviceInvoice): void {
  setTimeout(() => {
    if (invoice.status !== "pending") return;
    invoice.status = "paid";
    invoice.paid_at = new Date().toISOString();
    const order = orders.get(invoice.order_id);
    if (order) {
      order.status = "paid";
      order.paid_at = invoice.paid_at;
    }
    const venta = sales.find((s) => s.order_id === invoice.order_id);
    if (venta) {
      venta.status = "paid";
      venta.paid_at = invoice.paid_at;
    }
  }, AUTO_PAY_MS);
}

// ---------- router ----------
export interface DemoResponse {
  status: number;
  body: unknown;
}

function parsePath(path: string): { segs: string[]; query: URLSearchParams } {
  const [p, q] = path.split("?");
  return { segs: p.split("/").filter(Boolean), query: new URLSearchParams(q ?? "") };
}

const notFound: DemoResponse = { status: 404, body: { detail: "not_found" } };

export function handleApi(method: string, path: string, body?: unknown): DemoResponse {
  ensureSeeded();
  const { segs, query } = parsePath(path);
  const m = method.toUpperCase();
  const b = (body ?? {}) as Record<string, unknown>;

  // /api/exchange-rate — público y v1, pero es de donde el POS saca el "1 BTC = $X".
  if (segs[1] === "exchange-rate" && m === "GET") {
    return {
      status: 200,
      body: {
        mxn_per_btc: MXN_PER_BTC,
        sats_per_mxn: SATS_PER_MXN,
        fetched_at: Date.now() / 1000,
        source: "demo",
      },
    };
  }

  if (segs[1] !== "v2") return notFound;
  const r = segs.slice(2); // ruta relativa a /api/v2

  // POST /pairing/redeem
  if (r[0] === "pairing" && r[1] === "redeem" && m === "POST") {
    const code = String(b.code ?? "");
    if (!isDemoCode(code)) {
      return { status: 400, body: { detail: "pairing_invalid_or_expired" } };
    }
    return {
      status: 200,
      body: { device_token: DEMO_DEVICE_TOKEN, terminal: TERMINAL, tenant: TENANT },
    };
  }

  // GET /terminal/me
  if (r[0] === "terminal" && r[1] === "me" && m === "GET") {
    return {
      status: 200,
      body: {
        terminal_id: TERMINAL.id,
        name: TERMINAL.name,
        role: TERMINAL.role,
        tenant_id: TENANT.id,
        payment_methods: ["lightning", "cash"],
      },
    };
  }

  // GET /catalog/products
  if (r[0] === "catalog" && r[1] === "products" && m === "GET") {
    const barcode = query.get("barcode");
    const lista = barcode ? products.filter((p) => p.barcode === barcode) : products;
    return { status: 200, body: lista };
  }

  // ---- órdenes ----
  if (r[0] === "orders" && m === "POST" && !r[1]) {
    const items = (b.items ?? []) as {
      product_id?: string;
      description?: string;
      unit_price_mxn?: string;
      qty?: number;
    }[];
    if (items.length === 0) return { status: 400, body: { detail: "empty_order" } };
    let subtotal = 0;
    const lineas: DeviceSaleItem[] = [];
    for (const it of items) {
      const qty = it.qty ?? 1;
      const producto = it.product_id
        ? products.find((p) => p.id === it.product_id)
        : undefined;
      const unit = producto
        ? Number(producto.price_mxn)
        : Number(it.unit_price_mxn ?? 0);
      subtotal += unit * qty;
      lineas.push({
        product_id: producto?.id ?? null,
        description: producto?.name ?? String((it as { description?: string }).description ?? "Item"),
        qty,
        unit_price_mxn: money(unit),
        line_total_mxn: money(unit * qty),
      });
    }
    const discount = Number(b.discount_mxn ?? 0);
    if (discount < 0) return { status: 400, body: { detail: "negative_discount" } };
    if (discount > subtotal) return { status: 400, body: { detail: "discount_exceeds_subtotal" } };
    const order: DeviceOrder = {
      id: id("order"),
      status: "open",
      subtotal_mxn: money(subtotal),
      discount_mxn: money(discount),
      total_mxn: money(subtotal - discount),
      created_at: new Date().toISOString(),
      payment_method: "lightning",
      paid_at: null,
    };
    orders.set(order.id, order);
    orderItems.set(order.id, lineas);
    return { status: 201, body: order };
  }

  if (r[0] === "orders" && r[1] && r[2] === "invoice" && m === "POST") {
    const order = orders.get(r[1]);
    if (!order) return { status: 404, body: { detail: "order_not_found" } };
    const total = Number(order.total_mxn);
    const sats = mxnToSats(total);
    const invoice: DeviceInvoice = {
      id: id("inv"),
      order_id: order.id,
      status: "pending",
      amount_sats: sats,
      amount_mxn: order.total_mxn,
      fx_rate: money(MXN_PER_BTC),
      bolt11: fakeBolt11(sats),
      payment_hash: id("hash"),
      expires_at: new Date(Date.now() + INVOICE_TTL_MS).toISOString(),
      paid_at: null,
    };
    invoices.set(invoice.id, invoice);
    order.payment_method = "lightning";
    order.status = "invoiced";
    recordSale(order, invoice);
    scheduleAutoPay(invoice);
    return { status: 201, body: invoice };
  }

  if (r[0] === "orders" && r[1] && r[2] === "cash" && m === "POST") {
    const order = orders.get(r[1]);
    if (!order) return { status: 404, body: { detail: "order_not_found" } };
    if (order.status === "paid") return { status: 409, body: { detail: "order_already_paid" } };
    order.payment_method = "cash";
    order.status = "paid";
    order.paid_at = new Date().toISOString();
    recordSale(order, null);
    return { status: 200, body: order };
  }

  if (r[0] === "orders" && r[1] && !r[2] && m === "GET") {
    const order = orders.get(r[1]);
    return order ? { status: 200, body: order } : { status: 404, body: { detail: "order_not_found" } };
  }

  // GET /invoices/:id  — el POS la consulta por polling hasta que se confirma
  if (r[0] === "invoices" && r[1] && m === "GET") {
    const invoice = invoices.get(r[1]);
    if (!invoice) return { status: 404, body: { detail: "invoice_not_found" } };
    if (invoice.status === "pending" && Date.parse(invoice.expires_at) < Date.now()) {
      invoice.status = "expired";
    }
    return { status: 200, body: invoice };
  }

  // GET /sales
  if (r[0] === "sales" && m === "GET") {
    const limit = Number(query.get("limit") ?? 200);
    return { status: 200, body: sales.slice(0, limit) };
  }

  return notFound;
}
